<template>
    <div v-if="shownInstruction" class="game-instruction-backdrop">
        <div class="game-instruction control-panel-content">
            <div class="row content col-12" style="padding-bottom: 2em;">
                <div class="logout">
                    Ingelogd als <span>{{ teamDisplayName }}</span>
                    &nbsp;
                    |
                    &nbsp;
                    <a href="#" @click.prevent="logout">Uitloggen</a>
                </div>
            </div>
            <div v-if="!message" class="text-center p-3">
                <div class="spinner-border" role="status">
                    <span class="visually-hidden">Laden...</span>
                </div>
            </div>
            <div v-else>
                <div class="row">
                    <div class="col-lg-6 offset-lg-3">
                        <h2 v-if="message.subject" class="game-instruction-title">{{ message.subject }}</h2>
                        <div v-if="message.htmlBody" v-html="message.htmlBody"></div>

                        <div class="justify-content-center" v-if="!isLastStep">
                            <a href="/" class="admin-button small mb-4">
                                Naar de website van LeefGoed
                            </a>
                        </div>     

                        <TimeoutView v-if="isTimed" :durationSeconds="durationSeconds" :remainingSeconds="remainingSeconds"
                                message="resterend" />
                    </div>
                </div>               
            </div>

            <div v-if="!isTimed" class="game-instruction-actions">
                <ButtonView :text="isReplay ? 'Doorgaan' : 'Starten!'" @click="close" />
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/repository/api.js';
import { mapState, mapActions } from 'vuex';
import ButtonView from '@/components/controlpanel/ButtonView.vue';
import TimeoutView from '@/components/controlpanel/TimeoutView.vue';

export default {
    name: "ControlPanelGameInstruction",
    components: { ButtonView, TimeoutView },
    data() {
        return {
            now: Date.now(),
            clock: null,
            message: null,
            // Set from the "Speluitleg" menu entry (VUE_APP_SHOW_FIRST_STEP_IN_MENU)
            isReplayRequested: false,
        };
    },
    computed: {
        ...mapState(['session', 'sessionToken', 'isAdmin', 'team']),
        activeInstruction() {
            if (!this.sessionToken || this.isAdmin || !this.session || !this.session.steps) {
                return null;
            }
            return this.session.steps.find(step => {
                if (!step.isGameInstruction || !step.startTime) {
                    return false;
                }
                // Untimed instructions stay visible until the player closes (reads) them
                if (!(step.duration > 0) && step.isRead) {
                    return false;
                }
                if (step.teams && !step.teams.includes(this.team)) {
                    return false;
                }
                const start = new Date(step.startTime).getTime();
                if (this.now < start) {
                    return false;
                }
                // Timed instructions close automatically once their time is up
                if (step.duration > 0 && this.now >= start + step.duration) {
                    return false;
                }
                return true;
            }) || null;
        },
        // The first step of the scenario, shown again (untimed) from the "Speluitleg" menu entry
        replayInstruction() {
            if (!this.isReplayRequested || !this.sessionToken || this.isAdmin || !this.session || !this.session.steps) {
                return null;
            }
            return this.session.steps[0] || null;
        },
        // A scheduled instruction always takes precedence over the replay
        shownInstruction() {
            return this.activeInstruction || this.replayInstruction;
        },
        isReplay() {
            return !this.activeInstruction && !!this.replayInstruction;
        },
        isLastStep() {
            if (!this.shownInstruction) {
                return false;
            }
            const steps = this.session.steps;
            return !!this.shownInstruction.isEnd || steps[steps.length - 1] === this.shownInstruction;
        },
        isTimed() {
            return !!this.activeInstruction && this.activeInstruction.duration > 0;
        },
        durationSeconds() {
            return this.isTimed ? Math.floor(this.activeInstruction.duration / 1000) : 0;
        },
        remainingSeconds() {
            if (!this.isTimed) {
                return 0;
            }
            const end = new Date(this.activeInstruction.startTime).getTime() + this.activeInstruction.duration;
            return Math.max(0, Math.floor((end - this.now) / 1000));
        },
        teamDisplayName() {
            // if (textutils.useTeams && this.teamIndex >= 0) {
            //     return textutils.teamName(this.$store.state.team);
            // }
            return this.$store.state.userEmail;
        },
    },
    watch: {
        shownInstruction: {
            immediate: true,
            handler(step, oldStep) {
                const id = step ? step.dilemmaId : null;
                const oldId = oldStep ? oldStep.dilemmaId : null;
                if (id !== oldId) {
                    this.loadMessage(id);
                }
                // Prevent the page behind the overlay from scrolling
                document.body.style.overflow = step ? 'hidden' : '';
            },
        },
    },
    mounted() {
        this.clock = setInterval(() => {
            this.now = Date.now();
        }, 1000);
        window.addEventListener('show-game-instruction', this.showReplay);
    },
    beforeUnmount() {
        clearInterval(this.clock);
        window.removeEventListener('show-game-instruction', this.showReplay);
        document.body.style.overflow = '';
    },
    methods: {
        ...mapActions(['logoutUser']),
        async loadMessage(dilemmaId) {
            this.message = null;
            if (!dilemmaId) {
                return;
            }
            try {
                const response = await api.getMessage(this.sessionToken, dilemmaId);
                // Make sure the instruction did not change while loading
                if (this.shownInstruction && this.shownInstruction.dilemmaId === dilemmaId) {
                    this.message = response.data.data;
                }
            } catch (error) {
                console.error('Error loading game instruction:', error);
            }
        },
        showReplay() {
            this.isReplayRequested = true;
        },
        async close() {
            if (this.isReplay) {
                this.isReplayRequested = false;
                return;
            }
            if (!this.activeInstruction) {
                return;
            }
            const dilemmaId = this.activeInstruction.dilemmaId;
            // Mark as read locally first, so the overlay closes immediately
            this.$store.dispatch('markAsRead', dilemmaId);
            try {
                await api.emailOpened(this.sessionToken, dilemmaId);
            } catch (error) {
                console.error('Error marking game instruction as read:', error);
            }
        },
        logout() {
            if (window.confirm('Weet u zeker dat u wilt uitloggen?')) {
                this.logoutUser();
                this.$router.push('/');
            }
        }
    },
};
</script>

<style scoped>
.game-instruction-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1050;
    padding: 2em;
    background: rgba(0, 0, 0, 0.4);
    overscroll-behavior: contain;
    touch-action: none;
}

.game-instruction {
    position: relative;
    height: 100%;
    overflow-y: auto;
    overscroll-behavior: contain;
    touch-action: pan-y;
    background:
        linear-gradient(var(--color-primary-extra-light-alpha), var(--color-primary-extra-light-alpha)),
        white;
}

.game-instruction-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 2rem;
}
</style>
